/**
 * Query key convention.
 *
 * Keys are structured `[resource, scope?, ...identifiers]` so that a later
 * feature can invalidate precisely as much as it needs and no more:
 *
 *   queryKeys.session()                       -> ['session']
 *   queryKeys.clients.all()                   -> ['clients']
 *   queryKeys.clients.byBusinessUnit(id)      -> ['clients', 'business-unit', id]
 *   queryKeys.clients.detail(id)              -> ['clients', 'detail', id]
 *
 * Because TanStack Query matches keys by prefix, invalidating `['clients']`
 * clears every client query, while `['clients', 'business-unit', id]` clears
 * one unit's list and leaves the others cached.
 *
 * Scope matters more here than in most applications: what a user may see is
 * derived from the ownership hierarchy (phase 9), so a cached list is only
 * ever valid for the caller and scope that fetched it. Putting the scope in
 * the key is what keeps one user's data from being served to another.
 *
 * This is the whole convention. It is documented once, here and in the
 * frontend README, and every later feature follows it.
 */

export const queryKeys = {
  /** The caller's session, including the navigation they may be shown. */
  session: () => ['session'] as const,

  /**
   * The landing dashboard (f16.1). The caller's server-resolved scope is part
   * of the key, so a session reload that grants or revokes an assignment is a
   * new entry rather than a stale one; `['dashboard']` still clears them all.
   */
  dashboard: (scope = '') => ['dashboard', scope] as const,

  /** Platform health. Not polled by default. */
  health: () => ['health'] as const,

  /**
   * The audit log (f1.5). The list key carries the FULL filter set, so
   * changing any filter starts a new list from the newest entry -- a cursor
   * from one filter set is never replayed against another (the server would
   * refuse it by fingerprint anyway).
   */
  auditLog: {
    all: () => ['audit-log'] as const,
    list: (filters: Readonly<Record<string, unknown>>) =>
      ['audit-log', 'list', filters] as const,
    detail: (entryId: string) => ['audit-log', 'detail', entryId] as const,
    actors: (range: Readonly<Record<string, unknown>>) =>
      ['audit-log', 'actors', range] as const,
  },

  /**
   * The settings catalogue (f2.1). ONE key: the catalogue is read whole and a
   * save returns it whole, so the mutation replaces this entry directly.
   */
  settings: {
    catalog: () => ['settings', 'catalog'] as const,
  },

  /**
   * Reference data (f2.2). ONE key per collection, so adding a country never
   * refetches the lookup catalogue. The lookup catalogue is a single key
   * covering every registered type: it is read whole, and a value added to
   * one type has to be visible on that type's tab immediately.
   */
  /**
   * The user roster (f3.1). ONE key: the list is read whole and is not
   * paginated, so a role grant invalidates exactly this and nothing else.
   */
  users: {
    all: () => ['users'] as const,
  },

  referenceData: {
    lookups: () => ['reference-data', 'lookups'] as const,
    countries: () => ['reference-data', 'countries'] as const,
    resourceBuMappings: () => ['reference-data', 'resource-bu-mappings'] as const,
  },

  /** The Organization Overview: one collection, narrowed by the server. */
  organizationOverview: () => ['organization-overview'] as const,

  /**
   * Business Unit Groups (f4.1). ONE collection key: the server returns the
   * whole set, unpaginated, so every mutation invalidates exactly this.
   *
   * No scope segment yet. What the caller may see is decided entirely
   * server-side, and the session query is the only thing that changes when
   * their scope does.
   *
   * Settled by f9.2: within one session the caller does not change, so NO
   * scope segment is added. When an assignment changes, the ownership
   * mutations invalidate `session()` -- that is the invalidation point -- and
   * the server narrows the next list on its own authority regardless.
   */
  businessUnitGroups: {
    all: () => ['business-unit-groups'] as const,
    detail: (groupId: string) => ['business-unit-groups', 'detail', groupId] as const,
  },

  /**
   * Business Units (f5.1). The same ONE-collection shape as the level above,
   * for the same reason: the server returns the whole set, unpaginated.
   *
   * Every unit mutation invalidates the GROUPS collection too, because f5.1
   * delivered f4.1's deferred `business_unit_count` -- creating, deactivating
   * or reactivating a unit changes a number rendered on the other screen, and
   * leaving it alone would show a stale count until something else happened to
   * refetch.
   */
  businessUnits: {
    all: () => ['business-units'] as const,
    detail: (unitId: string) => ['business-units', 'detail', unitId] as const,
  },

  /**
   * Clients (f6.1) -- the worked example this file's own convention docstring
   * uses, now real.
   *
   * The same ONE-collection shape as the two levels above, for the same
   * reason: the server returns the whole set, unpaginated, and the screen's
   * page controls run client-side over 39 rows.
   *
   * Every client mutation invalidates the BUSINESS UNITS collection too,
   * because f6.1 delivered f5.1's deferred `client_count` -- creating,
   * deactivating or reactivating a client changes a number rendered on the
   * other screen, and leaving it alone would show a stale count until
   * something else happened to refetch.
   *
   * `byBusinessUnit` is declared because the convention names it and because
   * f7.1 and the Business Units screen's drill-through will want it. Nothing
   * fetches it today: the drill-through filters the ONE collection
   * client-side, because adding a server filter would break f4.1's
   * no-parameter assertion to narrow a list the browser already holds.
   */
  clients: {
    all: () => ['clients'] as const,
    byBusinessUnit: (unitId: string) =>
      ['clients', 'business-unit', unitId] as const,
    detail: (clientId: string) => ['clients', 'detail', clientId] as const,
    /** f11.4: the Commitments tab -- this client's cycles for the caller. */
    commitments: (clientId: string) => ['clients', 'commitments', clientId] as const,
  },

  /**
   * Engagements (f7.1) -- the fourth level, which has no screen of its own.
   *
   * `forClient` rather than `all`, and that is the shape rather than an
   * omission: there is no global collection to cache, because the server
   * exposes none and no screen lists engagements across clients. Following the
   * `[resource, scope?, ...identifiers]` convention this file documents, the
   * scope segment is the client.
   *
   * `elements` is keyed by ENGAGEMENT for the same reason: they are fetched
   * when a row is expanded, so one engagement's elements are never served for
   * another.
   *
   * Every engagement mutation invalidates the CLIENTS collection too, because
   * f7.1 delivered f6.1's deferred `engagement_count` -- creating,
   * deactivating or reactivating an engagement changes a number rendered on
   * the Clients screen and in this very tab's count badge, and leaving it
   * alone would show a stale count until something else happened to refetch.
   */
  engagements: {
    all: () => ['engagements'] as const,
    forClient: (clientId: string) =>
      ['engagements', 'client', clientId] as const,
    detail: (engagementId: string) =>
      ['engagements', 'detail', engagementId] as const,
    elements: (engagementId: string) =>
      ['engagements', 'elements', engagementId] as const,
  },

  /**
   * Delivery ownership (f4.2).
   *
   * The assignable accounts are ONE key: the list is read whole and
   * unpaginated, and nothing in this feature writes it -- an account is
   * created, deactivated or reactivated on the Users screen, which owns its
   * own keys.
   *
   * Owners are keyed by NODE, because the server takes the node as a required
   * selector rather than a filter -- there is no unqualified collection to
   * cache. Assigning or ending an owner therefore invalidates that node's
   * key, plus the group's detail and the group LIST, whose `DEM` and `CEM`
   * columns are resolved server-side and would otherwise go stale.
   */
  assignableUsers: {
    all: () => ['assignable-users'] as const,
  },

  owners: {
    all: () => ['owners'] as const,
    forNode: (nodeType: string, nodeId: string) =>
      ['owners', nodeType, nodeId] as const,
  },

  /**
   * Background jobs (f8.1). The `[resource, scope?, ...identifiers]` shape
   * this file documents, instantiated for the first thing in the application
   * that CHANGES ON ITS OWN.
   *
   * That is what makes the detail key matter more here than elsewhere. A
   * client list changes when somebody edits a client; a job changes while
   * nobody touches it, so `useJobProgress` polls this key and stops the
   * moment the state is terminal. Keying per job is what lets two imports be
   * watched at once without either overwriting the other's progress.
   *
   * The list key carries its filters, for the same reason the audit log's
   * does: a list of failed `ingestion.probe` jobs is not a subset of the
   * unfiltered list that can be served from it.
   */
  jobs: {
    all: () => ['jobs'] as const,
    list: (filters: Readonly<Record<string, unknown>>) =>
      ['jobs', 'list', filters] as const,
    detail: (jobId: string) => ['jobs', 'detail', jobId] as const,
  },

  /**
   * Ingestion batches (f8.1, f8.2).
   *
   * f8.1 declared detail only, because it built no screen. f8.2 adds the three
   * the Data import screens need, following the same
   * `[resource, scope?, ...identifiers]` shape as everything above.
   *
   * `stagedPlan` and `roleSlots` are keyed per BATCH and not per screen: two
   * batches may be staged at once -- the prototype draws the banner for it --
   * and one's plan must never be served for the other. Discarding a batch
   * invalidates the collection and that batch's three keys, and nothing else.
   *
   * `roleSlots` carries its state filter, for the reason the audit log's list
   * key carries its filters: a list of `UNMATCHED` slots is not a subset of
   * the unfiltered list that can be served from it.
   */
  ingestionBatches: {
    all: () => ['ingestion-batches'] as const,
    list: (filters: Readonly<Record<string, unknown>>) =>
      ['ingestion-batches', 'list', filters] as const,
    detail: (batchId: string) => ['ingestion-batches', 'detail', batchId] as const,
    stagedPlan: (batchId: string) =>
      ['ingestion-batches', 'staged-plan', batchId] as const,
    roleSlots: (batchId: string, states: readonly string[]) =>
      ['ingestion-batches', 'role-slots', batchId, states] as const,
  },

  /**
   * COM cycles (f10.2). The list key carries its filters; the preview key its
   * month. Creating a cycle invalidates `['cycles']` -- every list, detail and
   * preview -- because a new cycle can become another cycle's previous link.
   */
  cycles: {
    all: () => ['cycles'] as const,
    list: (filters: Readonly<Record<string, unknown>>) => ['cycles', 'list', filters] as const,
    preview: (year: number, month: number) => ['cycles', 'preview', year, month] as const,
    /** f13.1: one requested CSV export's status (the requester's own). */
    export: (cycleId: string, exportId: string) => ['cycles', 'export', cycleId, exportId] as const,
  },

  /**
   * One cycle's workspace (f10.3): its detail and its step rail. A step or
   * state change invalidates `['cycle', id]`, which covers both.
   */
  cycle: {
    detail: (cycleId: string) => ['cycle', cycleId] as const,
    workflow: (cycleId: string) => ['cycle', cycleId, 'workflow'] as const,
    /** Step 6 (f10.9): what a release would hand managers. */
    releaseReadiness: (cycleId: string) => ['cycle', cycleId, 'release-readiness'] as const,
    /** f12.3: what a lock would snapshot now, and the cycle's snapshot history. */
    freezePreview: (cycleId: string) => ['cycle', cycleId, 'freeze-preview'] as const,
    snapshots: (cycleId: string) => ['cycle', cycleId, 'snapshots'] as const,
    /** f14.2: the reconciliation queue -- its list (full filter set) and its summary. */
    reconciliation: (cycleId: string) => ['cycle', cycleId, 'reconciliation'] as const,
    reconciliationList: (cycleId: string, filters: Readonly<Record<string, unknown>>) =>
      ['cycle', cycleId, 'reconciliation', 'list', filters] as const,
    reconciliationSummary: (cycleId: string) => ['cycle', cycleId, 'reconciliation', 'summary'] as const,
    /** f14.3: the summary narrowed to one batch -- still under the queue prefix, so a decision refetches it. */
    reconciliationBatchSummary: (cycleId: string, batchId: string) =>
      ['cycle', cycleId, 'reconciliation', 'summary', batchId] as const,
    /** f13.2: the cycle's submission record (Finance and Admin). */
    submission: (cycleId: string) => ['cycle', cycleId, 'submission'] as const,
    /** f11.3: the rail's forecast-input entry for the caller. */
    inputEntry: (cycleId: string) => ['cycle', cycleId, 'input-entry'] as const,
    /** f11.1: the units with forecast lines the caller may see, and one unit's grid. */
    forecastUnits: (cycleId: string) => ['cycle', cycleId, 'forecast-grid', 'units'] as const,
    forecastGrid: (cycleId: string, businessUnitId: string) =>
      ['cycle', cycleId, 'forecast-grid', businessUnitId] as const,
    /** f11.6: the cycle's validation warnings, optionally narrowed to a unit and/or a client. */
    validationWarnings: (cycleId: string, businessUnitId = '', clientId = '') =>
      ['cycle', cycleId, 'validation-warnings', businessUnitId, clientId] as const,
    /** f12.1: the review screen. The unit ids are SORTED so a selection has one key; f12.2 writes here too. */
    reviewUnits: (cycleId: string) => ['review', cycleId, 'units'] as const,
    review: (cycleId: string, unitIds: readonly string[]) => ['review', cycleId, [...unitIds].sort()] as const,
    reviewComments: (cycleId: string, businessUnitId: string, clientId: string) =>
      ['review', cycleId, 'comments', businessUnitId, clientId] as const,
    /** f11.2: the master elements the caller may still add to one unit. */
    forecastElementCandidates: (cycleId: string, businessUnitId: string, q: string) =>
      ['cycle', cycleId, 'forecast-elements', businessUnitId, q] as const,
    /** Step 1 (f10.4): the batch list and one batch's review, under the cycle. */
    usEngagements: (cycleId: string) => ['cycle', cycleId, 'us-engagements'] as const,
    usEngagementsBatch: (cycleId: string, batchId: string) =>
      ['cycle', cycleId, 'us-engagements', batchId] as const,
    usEngagementsJob: (cycleId: string, jobId: string) =>
      ['cycle', cycleId, 'us-engagements', 'job', jobId] as const,
    billableDays: (cycleId: string) => ['cycle', cycleId, 'billable-days'] as const,
    billableDaysBatch: (cycleId: string, batchId: string) =>
      ['cycle', cycleId, 'billable-days', batchId] as const,
    billableDaysJob: (cycleId: string, jobId: string) =>
      ['cycle', cycleId, 'billable-days', 'job', jobId] as const,
    /** Step 3 (f10.6): scopes, batches, one batch's review and its jobs. */
    kimbleDump: (cycleId: string) => ['cycle', cycleId, 'kimble-dump'] as const,
    kimbleDumpScopes: (cycleId: string) => ['cycle', cycleId, 'kimble-dump', 'scopes'] as const,
    kimbleDumpBatch: (cycleId: string, batchId: string) => ['cycle', cycleId, 'kimble-dump', batchId] as const,
    kimbleDumpJob: (cycleId: string, jobId: string) => ['cycle', cycleId, 'kimble-dump', 'job', jobId] as const,
    salesRates: (cycleId: string) => ['cycle', cycleId, 'sales-rates'] as const,
    salesRateScopes: (cycleId: string) => ['cycle', cycleId, 'sales-rates', 'scopes'] as const,
    salesRateBatch: (cycleId: string, batchId: string) => ['cycle', cycleId, 'sales-rates', batchId] as const,
    salesRateJob: (cycleId: string, jobId: string) => ['cycle', cycleId, 'sales-rates', 'job', jobId] as const,
    /** Step 5 (f10.8): batches, one batch's review, its jobs, and the cycle's committed rates. */
    exchangeRates: (cycleId: string) => ['cycle', cycleId, 'exchange-rates'] as const,
    exchangeRateBatch: (cycleId: string, batchId: string) => ['cycle', cycleId, 'exchange-rates', batchId] as const,
    exchangeRateJob: (cycleId: string, jobId: string) =>
      ['cycle', cycleId, 'exchange-rates', 'job', jobId] as const,
    cycleExchangeRates: (cycleId: string) => ['cycle', cycleId, 'exchange-rates', 'current'] as const,
    /** Step 7 (f14.3): the step list, its jobs, and the f14.1 batch reads it shows. */
    actualsStep: (cycleId: string) => ['cycle', cycleId, 'actuals-step'] as const,
    actualsStepJob: (cycleId: string, jobId: string) => ['cycle', cycleId, 'actuals-step', 'job', jobId] as const,
    actualsBatch: (cycleId: string, batchId: string) => ['cycle', cycleId, 'actuals-step', 'batch', batchId] as const,
    actualsBatchRows: (cycleId: string, batchId: string, matchStatus: string) =>
      ['cycle', cycleId, 'actuals-step', 'batch', batchId, 'rows', matchStatus] as const,
  },

  /**
   * Budget reference data (f10.10). Finance/Admin only, never scoped: the
   * two tables are keyed by year, and the imports sit under their own prefix
   * so a commit invalidates `['budgets']` and refetches every figure.
   */
  budgets: {
    all: () => ['budgets'] as const,
    clientAnnual: (fiscalYear: number) => ['budgets', 'client-annual', fiscalYear] as const,
    unitMonthly: (fiscalYear: number) => ['budgets', 'unit-monthly', fiscalYear] as const,
    imports: () => ['budgets', 'imports'] as const,
    importBatch: (batchId: string) => ['budgets', 'imports', batchId] as const,
    importJob: (jobId: string) => ['budgets', 'imports', 'job', jobId] as const,
  },

  /**
   * Artifacts (f15.1). One list per owning node (the Artifacts tabs) and one
   * detail per artifact. A publish invalidates both prefixes and the owning
   * node's detail, whose `artifact_count` feeds the tab badge.
   */
  artifacts: {
    all: () => ['artifacts'] as const,
    /** f15.2: the top-level list across the hierarchy. */
    list: () => ['artifacts', 'list'] as const,
    /** f15.2: the nodes of one level the caller may publish on. */
    ownerOptions: (level: string) => ['artifacts', 'owner-options', level] as const,
    forOwner: (ownerType: string, ownerId: string) => ['artifacts', 'owner', ownerType, ownerId] as const,
    detail: (artifactId: string) => ['artifacts', 'detail', artifactId] as const,
    /** f15.4: labels and states of the artifact's access codes (never a plaintext). */
    accessPasswords: (artifactId: string) => ['artifacts', 'detail', artifactId, 'access-passwords'] as const,
    /** f15.6: the access history. Under the `artifacts` prefix, so every artifact mutation invalidates it. */
    accessEvents: (artifactId: string) => ['artifacts', artifactId, 'access-events'] as const,
  },
} as const;
